import React, { useCallback, useEffect, useState } from "react";
import { useLive } from "@/lib/live";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle, WarningCircle, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export function AccountsHealthCard() {
  const { user } = useAuth();
  const [h, setH] = useState(null);
  const [busy, setBusy] = useState(false);
  useLive(() => { load(); });
  const load = useCallback(async () => {
    try { setH((await api.get("/accounts/health")).data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Health check fail"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const [preview, setPreview] = useState(null);
  const openPreview = async () => {
    try { setPreview((await api.get("/accounts/reconciliation")).data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Report load fail"); }
  };
  const fix = async () => {
    if (!window.confirm("Sirf save kiye baaki/advance theek honge (payments nahi badlenge). Backup + audit log banega. Apply karein?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/accounts/reconciliation/apply");
      setPreview(null);
      toast.success(`Theek ho gaya: ${data.orders_fixed} orders, ${data.credits_fixed} advance (backup saved)`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Fix fail"); }
    setBusy(false);
  };

  if (!h) return null;
  return (
    <div className={`surface p-5 border-l-4 ${h.ok ? "border-l-emerald-500" : "border-l-red-500"}`} data-testid="accounts-health-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          {h.ok ? <CheckCircle size={22} weight="fill" className="text-emerald-500" /> : <WarningCircle size={22} weight="fill" className="text-red-500" />}
          <div>
            <div className="font-semibold text-slate-100" data-testid="accounts-health-status">
              {h.ok ? "Accounts health: sab hisaab match hai" : "Accounts health: saved numbers mein farak — admin approval baaki"}
            </div>
            <div className="text-xs text-slate-400">Total due {inr(h.total_receivable)} · Advance {inr(h.total_advance)} · {h.customers} customers</div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} className="rounded-sm border-slate-700 bg-slate-800 text-slate-100" data-testid="accounts-health-refresh">
            <ArrowsClockwise size={14} className="mr-1" /> Check again
          </Button>
          {!h.ok && (
            <Button size="sm" onClick={openPreview} className="btn-industrial rounded-sm" data-testid="accounts-health-preview">
              Reconciliation report dekho
            </Button>
          )}
        </div>
      </div>
      <div className="mt-3 grid sm:grid-cols-3 gap-2">
        {h.checks.map((c) => (
          <div key={c.key} className="flex items-center gap-2 text-sm" data-testid={`accounts-health-check-${c.key}`}>
            <span className={`w-2 h-2 rounded-full ${c.ok ? "bg-emerald-500" : "bg-red-500"}`} />
            <span className="text-slate-300">{c.label}</span>
            {!c.ok && c.count > 0 && <span className="text-red-500 font-medium">{c.count}</span>}
          </div>
        ))}
      </div>
      {h.same_mobile_many_names > 0 && (
        <div className="mt-3 text-xs text-amber-600" data-testid="accounts-health-names">
          {h.same_mobile_many_names} mobile numbers par alag-alag naam hain (e.g. {h.same_mobile_samples.slice(0, 3).map((x) => `${x.mobile}: ${x.names.join(" / ")}`).join(" · ")})
        </div>
      )}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto bg-slate-900 border-slate-700" data-testid="reconciliation-dialog">
          <DialogHeader><DialogTitle className="text-slate-100">Reconciliation report (sirf preview)</DialogTitle></DialogHeader>
          {preview && (
            <div className="space-y-3">
              <div className="text-sm text-slate-400">{preview.order_count} orders · {preview.advance_mismatch} customer advance · kul farak {inr(preview.total_difference)}</div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="reconciliation-table">
                  <thead><tr className="text-slate-500 text-left">
                    {["Order", "Customer", "Order total", "Valid payments", "Saved baaki", "Sahi baaki", "Farak", "Kaaran"].map((x) => <th key={x} className="py-1.5 pr-3 font-normal">{x}</th>)}
                  </tr></thead>
                  <tbody>
                    {preview.orders.slice(0, 300).map((r) => (
                      <tr key={r.order_id} className="border-t border-slate-700/70 text-slate-200">
                        <td className="py-1.5 pr-3 font-mono-num">{r.order_number}</td>
                        <td className="pr-3">{r.customer_name}</td>
                        <td className="pr-3">{inr(r.order_total)}</td>
                        <td className="pr-3">{inr(r.valid_payments)}</td>
                        <td className="pr-3">{inr(r.stored_balance)}</td>
                        <td className="pr-3 font-semibold">{inr(r.correct_balance)}</td>
                        <td className="pr-3 text-red-500">{inr(r.difference)}</td>
                        <td className="pr-3 text-slate-400">{r.cause}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {user?.role === "admin" ? (
                <Button onClick={fix} disabled={busy} className="btn-industrial rounded-sm" data-testid="accounts-health-fix">
                  {busy ? "Applying…" : "Confirm & Apply (backup + audit log)"}
                </Button>
              ) : <div className="text-xs text-slate-500">Apply sirf primary Admin kar sakta hai.</div>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
